import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { operatorService } from "../../../services";
import { UserInfo } from "../../../types/operator";
import LoadingSpinner from "../../common/LoadingSpinner";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";

const OperatorUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // 회원 목록 조회
  const fetchUsers = async (page: number) => {
    setIsLoading(true);
    try {
      const response = await operatorService.getUsers({
        page,
        limit,
      });

      if (response.success && response.data) {
        setUsers(response.data.users);
        setTotal(response.data.total);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.totalPages);
      } else {
        toast.error("회원 목록을 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("회원 목록을 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchUsers(1);
  }, []);

  // 페이지 변경
  const handlePageChange = (page: number) => {
    fetchUsers(page);
  };

  // 날짜 포맷
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR");
  };

  // 상태 뱃지 렌더링
  const renderStatusBadge = (user: UserInfo) => {
    if (user.deletedAt) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          탈퇴
        </span>
      );
    }
    if (user.isAnonymized) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          비식별화
        </span>
      );
    }
    if (user.isLoginBlocked) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
          로그인 차단
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        정상
      </span>
    );
  };

  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/operator/dashboard")}
            className="mr-3 text-gray-600 hover:text-gray-800"
          >
            <Icon name="chevronLeft" size="md" />
          </button>
          <h1 className="text-lg font-medium text-gray-800">
            개인정보 및 전송요구 상태 조회
          </h1>
        </div>
        <div className="text-sm text-gray-600">전체 {total}명</div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto">
          {/* 회원 목록 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Icon
                name="users"
                className="text-gray-400 mx-auto mb-4"
                size="xl"
              />
              <p className="text-gray-600">회원이 없습니다.</p>
            </div>
          ) : (
            <>
              {/* 카드 뷰 */}
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white rounded-lg shadow-sm p-4"
                  >
                    {/* 회원 기본 정보 */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-800 mb-1">
                          {user.userName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {user.telephoneNo}
                        </p>
                      </div>
                      {renderStatusBadge(user)}
                    </div>

                    {/* 상세 정보 */}
                    <div className="grid grid-cols-2 gap-2 mb-3 py-3 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">성별</p>
                        <p className="text-sm text-gray-800">{user.gender}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">생년월일</p>
                        <p className="text-sm text-gray-800">{user.birthDay}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500 mb-0.5">가입일</p>
                        <p className="text-sm text-gray-800">
                          {formatDate(user.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() =>
                          (window.location.href = `/operator/users/${user.id}/chats`)
                        }
                      >
                        <Icon name="messageSquare" className="mr-1" size="xs" />
                        질문 & 답변
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() =>
                          (window.location.href = `/operator/users/${user.id}/mydata`)
                        }
                      >
                        <Icon name="document" className="mr-1" size="xs" />
                        마이데이터
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    이전
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "primary" : "outline"
                          }
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    다음
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default OperatorUsersPage;
